/**
 * assets/nrn-warenkorb.js
 * Warenkorb Elemente des NOURION Konfigurators fuer Drawer und Warenkorbseite (Masterplan Abschnitt 4.5).
 * Wird auf allen Seiten ueber snippets/nrn-kopf.liquid als Modul geladen.
 *
 * <nrn-konfig-aktionen>  Markup aus snippets/nrn-warenkorb-konfig.liquid:
 *   Link "Im Konfigurator bearbeiten" und Knopf "Konfiguration entfernen" (alle Zeilen der Nummer).
 * <nrn-unternehmer>      Markup aus snippets/nrn-unternehmer.liquid:
 *   Pflicht Checkbox; speichert das Warenkorb Attribut "Unternehmer bestätigt" und sperrt bzw.
 *   entsperrt den Checkout Knopf derselben Warenkorb Zusammenfassung.
 *
 * Sichtbare Texte stehen ausschliesslich im Markup der Snippets; dieses Modul blendet nur ein und aus.
 * Horizon morpht Drawer und Warenkorbseite nach jeder Zeilenaenderung; beide Elemente sind deshalb
 * idempotent (connectedCallback, attributeChangedCallback) und lesen ihren Zustand neu.
 */
import { removeConfiguration, updateCartAttributes, fetchCart, closeCartDrawer } from '@theme/nrn-cart';

/** Name des Warenkorb Attributs (erscheint an der Bestellung unter "Zusaetzliche Details"). */
export const ATTRIBUT_UNTERNEHMER = 'Unternehmer bestätigt';

const SEL_CHECKBOX = 'input[data-nrn-unternehmer]';
const SEL_FEHLER = '[data-nrn-unternehmer-fehler]';
const SEL_SPEICHERFEHLER = '[data-nrn-unternehmer-speicherfehler]';
const SEL_CHECKOUT = 'button[name="checkout"]';
const SEL_GRENZE = 'cart-items-component, .shopify-section, body';
const STANDARD_HINWEIS_ID = 'NrnUnternehmerHinweis';
const STANDARD_AGB_ID = 'NrnUnternehmerAgb';

const SEL_KONFIG_ENTFERNEN = '[data-nrn-konfig-entfernen]';
const ATTR_ANSAGE = 'data-nrn-ansage';
/** Hoechste Wartezeit auf Horizons Morph nach dem Entfernen (ms). */
const MORPH_WARTEZEIT = 2000;

/** Basisklasse; in Node (Tests ohne DOM) eine leere Klasse, damit der Import nicht scheitert. */
const Basis = typeof HTMLElement !== 'undefined' ? HTMLElement : /** @type {any} */ (class {});

/** @param {() => void} fn */
function planen(fn) {
  if (typeof requestAnimationFrame === 'function') requestAnimationFrame(() => fn());
  else setTimeout(fn, 0);
}

/** @param {number} n */
function zweistellig(n) {
  return String(n).padStart(2, '0');
}

/**
 * Wert des Attributs "Unternehmer bestätigt": "Ja, TT.MM.JJJJ HH:MM" (Zeit in Europe/Berlin).
 * @param {Date} [datum]
 * @returns {string}
 */
export function unternehmerWert(datum = new Date()) {
  /** @type {Record<string, string>} */
  let teile = {};
  try {
    const format = new Intl.DateTimeFormat('de', {
      timeZone: 'Europe/Berlin',
      year: 'numeric',
      month: 'numeric',
      day: 'numeric',
      hour: 'numeric',
      minute: 'numeric',
      hourCycle: 'h23',
    });
    for (const teil of format.formatToParts(datum)) {
      if (teil.type !== 'literal') teile[teil.type] = teil.value;
    }
  } catch {
    teile = {};
  }
  if (!teile.year || !teile.month || !teile.day || teile.hour == null || teile.minute == null) {
    teile = {
      year: String(datum.getFullYear()),
      month: String(datum.getMonth() + 1),
      day: String(datum.getDate()),
      hour: String(datum.getHours()),
      minute: String(datum.getMinutes()),
    };
  }
  const stunde = Number(teile.hour) % 24;
  return `Ja, ${zweistellig(teile.day)}.${zweistellig(teile.month)}.${teile.year} ${zweistellig(stunde)}:${zweistellig(teile.minute)}`;
}

/** Seite neu laden (Ansicht passt nicht mehr zum Warenkorb). */
function neuLaden() {
  if (typeof location !== 'undefined' && typeof location.reload === 'function') location.reload();
}

/** @returns {Promise<void>} naechster Frame (in Node: naechster Timer) */
function naechsterFrame() {
  return new Promise((aufloesen) => planen(aufloesen));
}

/* ================================================================== Fokus und Ansage nach dem Entfernen */

/**
 * Suchbereich fuer Entfernen Knoepfe: der Drawer Dialog, auf der Warenkorbseite main (der Drawer liegt
 * ausserhalb), sonst das Dokument.
 * @param {any} dialog
 * @returns {any}
 */
function suchbereich(dialog) {
  if (dialog) return dialog;
  return document.querySelector('main') ?? document;
}

/**
 * Steht noch eine Gruppenaktion dieser Nummer im Dokument? Dann hat Horizon noch nicht gemorpht.
 * @param {string} nummer
 */
function konfigNochSichtbar(nummer) {
  if (typeof document === 'undefined' || typeof document.querySelectorAll !== 'function') return false;
  for (const element of Array.from(document.querySelectorAll('nrn-konfig-aktionen'))) {
    const wert = String(/** @type {any} */ (element).dataset?.nrnCfg ?? '').replace(/\s+/g, '');
    if (wert === nummer && element.isConnected) return true;
  }
  return false;
}

/**
 * Fokusziel nach dem Entfernen: Entfernen Knopf an derselben Position (also die naechste Konfiguration),
 * sonst der vorherige, sonst die Drawer Ueberschrift bzw. die h1 der Warenkorbseite.
 * @param {any} dialog Drawer Dialog oder null (Warenkorbseite)
 * @param {number} index Position des entfernten Knopfs unter allen Entfernen Knoepfen
 * @returns {{ ziel: any, ueberschrift: boolean } | null}
 */
function fokusZiel(dialog, index) {
  const wurzel = suchbereich(dialog);
  const knoepfe = Array.from(wurzel.querySelectorAll(SEL_KONFIG_ENTFERNEN)).filter((k) => /** @type {any} */ (k).isConnected);
  if (knoepfe.length > 0) {
    const position = Math.min(Math.max(index, 0), knoepfe.length - 1);
    return { ziel: knoepfe[position], ueberschrift: false };
  }
  if (dialog) {
    const ueberschrift = dialog.querySelector('#cart-drawer-heading');
    return ueberschrift ? { ziel: ueberschrift, ueberschrift: true } : { ziel: dialog, ueberschrift: true };
  }
  const h1 = wurzel.querySelector('h1');
  return h1 ? { ziel: h1, ueberschrift: true } : null;
}

/**
 * Schreibt den Text in eine verborgene Statusregion (role status, aria-live polite). Die Region liegt
 * direkt im offenen Drawer Dialog (sonst waere sie hinter dem modalen Dialog stumm) bzw. am body
 * und wird wiederverwendet.
 * @param {any} dialog
 * @param {string} text
 */
function ansagen(dialog, text) {
  if (!text || typeof document === 'undefined' || typeof document.createElement !== 'function') return;
  const wirt = dialog && dialog.isConnected && dialog.open ? dialog : document.body;
  if (!wirt) return;
  let region = Array.from(wirt.children ?? []).find((kind) => /** @type {any} */ (kind).hasAttribute?.(ATTR_ANSAGE));
  if (!region) {
    region = document.createElement('div');
    region.className = 'visually-hidden';
    region.setAttribute('role', 'status');
    region.setAttribute('aria-live', 'polite');
    region.setAttribute(ATTR_ANSAGE, '');
    wirt.appendChild(region);
  }
  const ziel = region;
  ziel.textContent = '';
  // Kurz warten, damit Screenreader eine neu angelegte oder geleerte Region als Aenderung erkennen.
  setTimeout(() => {
    ziel.textContent = text;
  }, 150);
}

/**
 * Nach erfolgreichem Entfernen: auf Horizons Morph warten (das Promise von removeConfiguration loest
 * vorher auf), dann Fokus setzen und den Text aus data-nrn-entfernt-text ansagen.
 * @param {{ nummer: string, dialog: any, index: number, knopf: any, text: string }} stand
 */
async function nachEntfernen({ nummer, dialog, index, knopf, text }) {
  const ende = Date.now() + MORPH_WARTEZEIT;
  while (konfigNochSichtbar(nummer) && Date.now() < ende) await naechsterFrame();
  await naechsterFrame();
  await naechsterFrame();

  const drawer = dialog && dialog.isConnected ? dialog : null;
  // Drawer inzwischen geschlossen: Fokus nicht verschieben.
  const drawerZu = Boolean(dialog) && !(drawer && drawer.open);
  const aktiv = /** @type {any} */ (document.activeElement);
  const fokusVerloren = !aktiv || aktiv === document.body || aktiv === knopf || !aktiv.isConnected;
  if (!drawerZu && fokusVerloren) {
    const treffer = fokusZiel(drawer, index);
    if (treffer?.ziel && typeof treffer.ziel.focus === 'function') {
      if (treffer.ueberschrift && !treffer.ziel.hasAttribute('tabindex')) treffer.ziel.setAttribute('tabindex', '-1');
      treffer.ziel.focus();
    }
  }
  ansagen(drawer, text);
}

/* ================================================================== Im Konfigurator bearbeiten */

/**
 * Ziel eines Bearbeiten Links, wenn es diese Seite ist (gleiche Herkunft, gleicher Pfad und gleiche Abfrage), sonst null.
 * @param {any} link
 * @returns {URL | null}
 */
function zielAufDieserSeite(link) {
  if (typeof location === 'undefined' || !location?.href) return null;
  try {
    const ziel = new URL(String(link.getAttribute?.('href') ?? link.href ?? ''), location.href);
    const hier = new URL(location.href);
    if (ziel.origin !== hier.origin || ziel.pathname !== hier.pathname || ziel.search !== hier.search) return null;
    return ziel;
  } catch {
    return null;
  }
}

/**
 * "Im Konfigurator bearbeiten" auf der Konfigurator Seite selbst: Der Link hat denselben Pfad und meist denselben
 * Hash, den der Konfigurator schon per replaceState gesetzt hat. Der Browser springt dann nicht, es kommt kein
 * hashchange, der Drawer bleibt offen und der Klick wirkt tot (Prüfung 16.10.2026, Befund 19). Darum hier den Drawer
 * schliessen und den Hash selbst setzen; weicht er ab, laedt der Konfigurator die Konfiguration aus dem hashchange.
 * @param {URL} ziel
 */
function bearbeitenAufDieserSeite(ziel) {
  closeCartDrawer();
  if (!ziel.hash || ziel.hash === location.hash) return;
  const vorher = location.href;
  try {
    history.pushState(history.state, '', ziel.href);
  } catch {
    location.hash = ziel.hash;
    return;
  }
  if (typeof window === 'undefined' || typeof window.dispatchEvent !== 'function') return;
  const ereignis = typeof HashChangeEvent === 'function'
    ? new HashChangeEvent('hashchange', { oldURL: vorher, newURL: location.href })
    : new Event('hashchange');
  window.dispatchEvent(ereignis);
}

/* ================================================================== nrn-konfig-aktionen */

export class NrnKonfigAktionen extends Basis {
  #laeuft = false;

  /** @param {Event} event */
  #beiKlick = (event) => {
    const ziel = /** @type {any} */ (event.target);
    const bearbeiten = ziel && typeof ziel.closest === 'function' ? ziel.closest('[data-nrn-konfig-bearbeiten]') : null;
    if (bearbeiten && this.contains(bearbeiten)) {
      // Neuer Tab oder Fenster (Strg, Cmd, Umschalt, mittlere Taste) bleibt dem Browser ueberlassen.
      const e = /** @type {any} */ (event);
      if (e.defaultPrevented || e.button > 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
      const zielUrl = zielAufDieserSeite(bearbeiten);
      if (!zielUrl) return;
      event.preventDefault();
      bearbeitenAufDieserSeite(zielUrl);
      return;
    }
    const knopf = ziel && typeof ziel.closest === 'function' ? ziel.closest('[data-nrn-konfig-entfernen]') : null;
    if (!knopf || !this.contains(knopf)) return;
    event.preventDefault();
    this.entfernen();
  };

  connectedCallback() {
    this.addEventListener('click', this.#beiKlick);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#beiKlick);
  }

  /** Konfigurationsnummer ohne Leerzeichen aus data-nrn-cfg. */
  get nummer() {
    return String(this.dataset.nrnCfg ?? '').replace(/\s+/g, '');
  }

  /**
   * Entfernt alle Zeilen dieser Konfiguration. Horizon aktualisiert danach Drawer und Warenkorbseite
   * ueber das CartLinesUpdateEvent; dabei verschwindet auch dieses Element.
   * @returns {Promise<void>}
   */
  async entfernen() {
    const nummer = this.nummer;
    if (this.#laeuft || nummer === '') return;
    /** @type {any} */
    const knopf = this.querySelector('[data-nrn-konfig-entfernen]');
    /** @type {any} */
    const fehler = this.querySelector('[data-nrn-konfig-fehler]');
    // Vor dem Entfernen lesen: Ansagetext aus dem Markup, Drawer Dialog und Position des Knopfs.
    const text = String(knopf?.dataset?.nrnEntferntText ?? '').trim();
    /** @type {any} */
    const dialog = typeof this.closest === 'function' ? this.closest('dialog') : null;
    const alleKnoepfe = Array.from(suchbereich(dialog).querySelectorAll(SEL_KONFIG_ENTFERNEN));
    const index = knopf ? alleKnoepfe.indexOf(knopf) : -1;

    this.#laeuft = true;
    this.setAttribute('aria-busy', 'true');
    if (knopf) knopf.setAttribute('aria-disabled', 'true');
    if (fehler) fehler.hidden = true;

    let fehlgeschlagen = false;
    let entfernt = false;
    try {
      const cart = await removeConfiguration(nummer, { source: this });
      // null: Keine Zeile dieser Nummer mehr im Warenkorb, die Ansicht ist veraltet.
      if (cart === null) neuLaden();
      else entfernt = true;
    } catch (fehlerObjekt) {
      fehlgeschlagen = true;
      console.error('[nrn-warenkorb] removing configuration failed', fehlerObjekt);
    } finally {
      this.#laeuft = false;
      this.removeAttribute('aria-busy');
      if (knopf) knopf.removeAttribute('aria-disabled');
    }

    if (fehlgeschlagen && this.isConnected) {
      if (fehler) fehler.hidden = false;
      if (knopf && typeof knopf.focus === 'function') knopf.focus();
    }

    if (entfernt) {
      nachEntfernen({ nummer, dialog, index, knopf, text }).catch((fehlerObjekt) =>
        console.warn('[nrn-warenkorb] focus after removal failed', fehlerObjekt)
      );
    }
  }
}

/* ================================================================== nrn-unternehmer */

/** @type {Set<NrnUnternehmer>} */
const instanzen = new Set();
/** Letzter vom Server bestaetigter Stand dieser Seite (null = unbekannt, dann gilt das Markup). */
/** @type {boolean | null} */
let bestaetigtBekannt = null;
let anfrageNummer = 0;
let speichertGerade = false;
let globalInstalliert = false;

/** @param {any} cart */
function standAusCart(cart) {
  const wert = cart?.attributes?.[ATTRIBUT_UNTERNEHMER];
  return typeof wert === 'string' ? wert.trim() !== '' : Boolean(wert);
}

/** Alle Instanzen mit dem bekannten Stand abgleichen. */
function alleAbgleichen() {
  for (const instanz of instanzen) {
    if (bestaetigtBekannt !== null) instanz.dataset.nrnBestaetigt = String(bestaetigtBekannt);
    instanz.abgleichen();
  }
}

/**
 * Klick auf einen gesperrten Checkout Knopf zeigt die Fehlermeldung.
 * Der gesperrte Knopf hat pointer-events: none (Stil im Snippet nrn-unternehmer), daher trifft der
 * Klick den umgebenden Knopfbereich (.cart__ctas) selbst. Klicks auf andere Kinder des Bereichs
 * (etwa die Checkbox, die dort ebenfalls sitzt) loesen keine Meldung aus.
 * @param {Event} event
 */
function beiKnopfbereich(event) {
  const ziel = /** @type {any} */ (event.target);
  if (!ziel) return;
  for (const instanz of instanzen) {
    const knopf = instanz.checkoutKnopf();
    if (!knopf || !knopf.disabled) continue;
    const bereich = knopf.parentElement ?? knopf;
    const aufKnopf = ziel === knopf || (typeof knopf.contains === 'function' && knopf.contains(ziel));
    if (aufKnopf || ziel === bereich) instanz.meldeFehlendeBestaetigung();
  }
}

/** Zurueck aus dem Browser Cache: echten Stand lesen. */
function beiSeitenanzeige(/** @type {any} */ event) {
  if (!event?.persisted || instanzen.size === 0) return;
  fetchCart()
    .then((cart) => {
      bestaetigtBekannt = standAusCart(cart);
      alleAbgleichen();
    })
    .catch((fehler) => console.warn('[nrn-warenkorb] cart refresh failed', fehler));
}

function globalInstallieren() {
  if (globalInstalliert) return;
  globalInstalliert = true;
  if (typeof document !== 'undefined' && document && typeof document.addEventListener === 'function') {
    document.addEventListener('click', beiKnopfbereich, true);
  }
  if (typeof window !== 'undefined' && window && typeof window.addEventListener === 'function') {
    window.addEventListener('pageshow', beiSeitenanzeige);
  }
}

export class NrnUnternehmer extends Basis {
  static observedAttributes = ['data-nrn-bestaetigt'];

  #letzteMeldung = 0;

  /** @param {Event} event */
  #beiAenderung = (event) => {
    const ziel = /** @type {any} */ (event.target);
    if (!ziel || typeof ziel.hasAttribute !== 'function' || !ziel.hasAttribute('data-nrn-unternehmer')) return;
    this.speichern(Boolean(ziel.checked));
  };

  connectedCallback() {
    globalInstallieren();
    instanzen.add(this);
    this.addEventListener('change', this.#beiAenderung);
    this.abgleichen();
  }

  disconnectedCallback() {
    instanzen.delete(this);
    this.removeEventListener('change', this.#beiAenderung);
  }

  /**
   * Nach einem Morph mit veraltetem Markup (Attribut anders als der bekannte Stand) neu abgleichen,
   * sobald Horizon auch den Checkout Knopf ersetzt hat.
   * @param {string} _name
   * @param {string | null} alt
   * @param {string | null} neu
   */
  attributeChangedCallback(_name, alt, neu) {
    if (alt === neu || !this.isConnected) return;
    planen(() => this.abgleichen());
  }

  /** Stand aus dem Markup (serverseitig aus cart.attributes gerendert). */
  get standImMarkup() {
    return this.dataset.nrnBestaetigt === 'true';
  }

  /** Wirksamer Stand: zuletzt vom Server bestaetigt, sonst Markup. */
  get bestaetigt() {
    return bestaetigtBekannt ?? this.standImMarkup;
  }

  /** Warenkorb leer (Knopf bleibt gesperrt, keine Fehlermeldung). */
  get leer() {
    return this.dataset.nrnLeer === 'true';
  }

  /** @returns {any} */
  get checkbox() {
    return this.querySelector(SEL_CHECKBOX);
  }

  /**
   * Checkout Knopf derselben Warenkorb Zusammenfassung: naechster Vorfahre, der einen enthaelt,
   * hoechstens bis zur Grenze cart-items-component bzw. Section.
   * @returns {any}
   */
  checkoutKnopf() {
    /** @type {any} */
    let element = this.parentElement;
    while (element) {
      const knopf = typeof element.querySelector === 'function' ? element.querySelector(SEL_CHECKOUT) : null;
      if (knopf) return knopf;
      if (typeof element.matches === 'function' && element.matches(SEL_GRENZE)) return null;
      element = element.parentElement;
    }
    return null;
  }

  /** Checkbox und Checkout Knopf an den wirksamen Stand angleichen. */
  abgleichen() {
    const bestaetigt = this.bestaetigt;
    const checkbox = this.checkbox;
    if (checkbox && !speichertGerade) checkbox.checked = bestaetigt;
    if (bestaetigt) this.#verberge(SEL_FEHLER, true);

    const knopf = this.checkoutKnopf();
    if (!knopf) return;
    const frei = bestaetigt && !speichertGerade && !this.leer;
    knopf.disabled = !frei;
    const hinweisId = this.querySelector(SEL_FEHLER)?.id || STANDARD_HINWEIS_ID;
    const ids = String(knopf.getAttribute('aria-describedby') ?? '')
      .split(/\s+/)
      .filter((id) => id !== '' && id !== hinweisId);
    if (!frei) ids.push(hinweisId);
    if (ids.length > 0) knopf.setAttribute('aria-describedby', ids.join(' '));
    else knopf.removeAttribute('aria-describedby');
  }

  /** Fehlermeldung "Bitte bestätigen Sie ..." zeigen und Fokus auf die Checkbox setzen. */
  meldeFehlendeBestaetigung() {
    if (this.bestaetigt || speichertGerade || this.leer) return;
    /** @type {any} */
    const fehler = this.querySelector(SEL_FEHLER);
    if (!fehler) return;
    const jetzt = Date.now();
    if (!fehler.hidden && jetzt - this.#letzteMeldung < 600) return;
    this.#letzteMeldung = jetzt;
    fehler.hidden = false;
    const checkbox = this.checkbox;
    if (checkbox) {
      checkbox.setAttribute('aria-invalid', 'true');
      this.#beschreibung(true);
      if (typeof checkbox.focus === 'function') checkbox.focus();
    }
  }

  /**
   * aria-describedby der Checkbox: immer der AGB Absatz, solange die Fehlermeldung sichtbar ist
   * zusaetzlich die Meldung. Nicht dauerhaft im Markup, weil ein per Referenz genanntes verborgenes
   * Element sonst immer vorgelesen wuerde.
   * @param {boolean} mitHinweis
   */
  #beschreibung(mitHinweis) {
    const checkbox = this.checkbox;
    if (!checkbox) return;
    const hinweisId = this.querySelector(SEL_FEHLER)?.id || STANDARD_HINWEIS_ID;
    checkbox.setAttribute('aria-describedby', mitHinweis ? `${STANDARD_AGB_ID} ${hinweisId}` : STANDARD_AGB_ID);
  }

  /**
   * Speichert die Bestaetigung als Warenkorb Attribut. Der Knopf wird erst frei, wenn der Server
   * den Wert bestaetigt hat; beim Abwaehlen ist er sofort gesperrt.
   * @param {boolean} wunsch
   * @returns {Promise<void>}
   */
  async speichern(wunsch) {
    const nummer = ++anfrageNummer;
    speichertGerade = true;
    for (const instanz of instanzen) {
      instanz.#verberge(SEL_SPEICHERFEHLER, false);
      if (wunsch) instanz.#verberge(SEL_FEHLER, true);
      instanz.setAttribute('aria-busy', 'true');
      instanz.abgleichen();
    }

    let fehlgeschlagen = false;
    try {
      const cart = await updateCartAttributes({ [ATTRIBUT_UNTERNEHMER]: wunsch ? unternehmerWert() : '' });
      if (nummer === anfrageNummer) bestaetigtBekannt = standAusCart(cart);
    } catch (fehler) {
      if (nummer === anfrageNummer) {
        fehlgeschlagen = true;
        console.error('[nrn-warenkorb] saving business confirmation failed', fehler);
      }
    }

    if (nummer !== anfrageNummer) return; // eine neuere Aenderung gilt
    speichertGerade = false;
    for (const instanz of instanzen) instanz.removeAttribute('aria-busy');
    alleAbgleichen();
    if (fehlgeschlagen) {
      /** @type {any} */
      const meldung = this.querySelector(SEL_SPEICHERFEHLER);
      if (meldung) meldung.hidden = false;
    }
  }

  /**
   * @param {string} selektor
   * @param {boolean} mitCheckbox aria-invalid der Checkbox zuruecksetzen
   */
  #verberge(selektor, mitCheckbox) {
    /** @type {any} */
    const element = this.querySelector(selektor);
    if (element) element.hidden = true;
    if (mitCheckbox) this.checkbox?.setAttribute('aria-invalid', 'false');
    if (selektor === SEL_FEHLER) this.#beschreibung(false);
  }
}

if (typeof customElements !== 'undefined' && typeof HTMLElement !== 'undefined') {
  if (!customElements.get('nrn-konfig-aktionen')) customElements.define('nrn-konfig-aktionen', NrnKonfigAktionen);
  if (!customElements.get('nrn-unternehmer')) customElements.define('nrn-unternehmer', NrnUnternehmer);
}
